import {Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe} from '@jsverse/transloco';
import {AppDatePipe} from '@frontend/shared';
import {Router} from '@angular/router';
import {
  AcceptTenderRequestParams,
  ProviderTenderControllerService,
  RejectTenderRequestParams,
  TenderDetailResponse,
  TenderPhaseType,
  TenderStatus,
} from '@frontend/shared-api';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {RadioButton} from 'primeng/radiobutton';
import {Button} from 'primeng/button';
import {APP_ROUTES} from '../../../../../app.routes';
import {SubsectionTitleComponent} from '../../../../../shared/components/header/subsection-title/subsection-title.component';
import {TenderRejectionReasonComponent} from '../tender-rejection-reason/tender-rejection-reason.component';

@Component({
  selector: 'app-tender-decision',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    RadioButton,
    Button,
    SubsectionTitleComponent,
    TenderRejectionReasonComponent,
    AppDatePipe,
  ],
  templateUrl: './tender-decision.component.html',
})
export class TenderDecisionComponent {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly providerTenderController = inject(ProviderTenderControllerService);

  readonly tenderDetails = input.required<TenderDetailResponse>();
  readonly tenderPhase = input.required<TenderPhaseType>();
  readonly acceptButtonKey = input('provider.tender.confirmation.acceptButtonShort');

  readonly accepted = output<void>();

  protected readonly decisionForm = this.fb.group({
    decision: ['decline'],
    tenderRejectionReasonId: [''],
  });

  protected readonly decisionControl = this.decisionForm.controls.decision;

  private readonly decisionValue = toSignal(this.decisionControl.valueChanges, {
    initialValue: 'decline',
  });

  private readonly rejectionReasonValue = toSignal(
    this.decisionForm.controls.tenderRejectionReasonId.valueChanges,
    {initialValue: ''},
  );

  protected readonly tenderId = computed(() => this.tenderDetails().tenderId);
  protected readonly submissionDate = computed(() => this.tenderDetails().submissionDate);
  protected readonly isAccepting = computed(() => this.decisionValue() === 'accept');
  protected readonly hasRejectionReason = computed(() => !!this.rejectionReasonValue());

  protected readonly dynamicAcceptText = computed(() => {
    return `provider.tender.confirmation.${this.tenderPhase().toLowerCase()}.acceptText`;
  });

  protected readonly dynamicDeclineText = computed(() => {
    return `provider.tender.confirmation.${this.tenderPhase().toLowerCase()}.declineText`;
  });

  private readonly alreadyAccepted = computed(
    () => this.tenderDetails().status === TenderStatus.ACCEPTED,
  );

  protected async acceptTender(): Promise<void> {
    if (!this.alreadyAccepted()) {
      const request: AcceptTenderRequestParams = {
        tenderId: this.tenderId(),
        tenderPhase: this.tenderPhase(),
      };
      await firstValueFrom(this.providerTenderController.acceptTender(request));
    }
    this.accepted.emit();
  }

  protected async rejectTender(): Promise<void> {
    const request: RejectTenderRequestParams = {
      tenderId: this.tenderId(),
      tenderRejectionRequest: {
        tenderPhase: this.tenderPhase(),
        reasonDirectusUuid: this.decisionForm.controls.tenderRejectionReasonId.value ?? '',
      },
    };
    await firstValueFrom(this.providerTenderController.rejectTender(request));
    await this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
  }
}
